import { useRef, useState } from 'react';
import type { DemoScript, Fx, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, OK, TYPE, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';

/**
 * NAVIGATOR · SET THE HEADING — a compass needle sweeps round; tap to stop it on the marked heading.
 *
 * The mark is a lit wedge on the dial with a notch on the rim; tap anywhere while the needle is
 * inside it. Two headings a round, each new one set well ahead of the needle so there is time to
 * see it coming. Round 1: a wide mark and a slow needle; round 2 a little narrower and quicker;
 * round 3 narrower and quicker again. The mark brightens while the needle is in it. A tap with the
 * needle outside the mark is the mistake (a hair outside still counts as in). The instrument
 * follows the era: glass, neon, cracked, brass or wood.
 *
 * The reference for a converted board (kit.tsx): the rules as plain functions, a small view state
 * (`HeadingView`), a pure `View`, the `Board` that plays it, and the `demo` the how-to card plays.
 */

export interface HeadingLevel {
  stops: number;
  /** The mark's width, degrees. */
  mark: number;
  /** The needle's speed, degrees a second. */
  speed: number;
  /** The needle turns back now and then (no round does since 6 Oct 2026). */
  turns: boolean;
  /** The mark brightens while the needle is in it. */
  hint: boolean;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const HEADING_LEVELS: readonly HeadingLevel[] = easier([
  { stops: 2, mark: 50, speed: 95, turns: false, hint: true },
  { stops: 2, mark: 38, speed: 120, turns: false, hint: true },
]);

/** A tap this many degrees outside the mark still counts as in it. */
export const GRACE_DEG = 4;
/** The needle turns back only this far from the mark, or more. */
export const TURN_CLEAR_DEG = 80;
/** After a heading is set, the needle rests on it this long before the next mark. */
const LOCK_MS = 650;
/** After a miss, taps wait this long. */
const COOLDOWN_MS = 350;
/** A miss shows on the dial this long. */
const MISS_SHOW_MS = 900;

export const norm = (a: number) => ((a % 360) + 360) % 360;

/** The shorter way round between two headings, 0–180. */
export function angleDist(a: number, b: number): number {
  const d = Math.abs(norm(a) - norm(b));
  return d > 180 ? 360 - d : d;
}

/** Is the needle on a mark `width` wide? */
export function onMark(needle: number, mark: number, width: number): boolean {
  return angleDist(needle, mark) <= width / 2 + GRACE_DEG;
}

/** The next heading: 130–240° ahead of the needle the way it turns, so there's time to see it come. */
export function nextMark(rand: () => number, needle: number, dir: 1 | -1): number {
  return norm(needle + dir * (130 + rand() * 110));
}

/** May the needle turn back here? Only well clear of the mark, so its last approach is steady. */
export function mayTurn(needle: number, mark: number): boolean {
  return angleDist(needle, mark) > TURN_CLEAR_DEG;
}

/** Most turns on the way to one mark. */
export const TURNS_PER_MARK = 2;

export interface Needle {
  deg: number;
  dir: 1 | -1;
  mark: number;
  /** When it may next turn back, on the game's clock. */
  turnAt: number | null;
  /** Turns left on the way to this mark. */
  turnsLeft: number;
}

/** One frame of the needle: it sweeps, and in a round that turns, now and then turns back, well clear of the mark. */
export function stepNeedle(n: Needle, dt: number, now: number, L: HeadingLevel, rand: () => number): void {
  n.deg = norm(n.deg + n.dir * L.speed * dt);
  if (!L.turns) return;
  // The first turn comes soon after a new mark, the next a little later.
  if (n.turnAt === null) n.turnAt = now + 450 + rand() * 700;
  if (now >= n.turnAt && n.turnsLeft > 0 && mayTurn(n.deg, n.mark)) {
    n.dir = n.dir === 1 ? -1 : 1;
    n.turnsLeft -= 1;
    n.turnAt = now + 800 + rand() * 900;
  }
}

/** A new mark for the needle, ahead of it. */
export function setMark(n: Needle, rand: () => number): void {
  n.mark = nextMark(rand, n.deg, n.dir);
  n.turnsLeft = TURNS_PER_MARK;
  n.turnAt = null;
}

/** Everything the dial shows. */
export interface HeadingView {
  /** The game clock, ms. */
  t: number;
  /** The needle, degrees (0 up, clockwise). */
  d: number;
  /** The mark's middle and its width, degrees. */
  m: number;
  w: number;
  /** Headings set this round, and how many to set. */
  n: number;
  of: number;
  /** The mark brightens while the needle is in it. */
  hint: boolean;
  /** 0 sweeping, 1 just stopped on the mark, 2 every heading set. */
  s: 0 | 1 | 2;
  /** The last miss, while it shows: where the needle stood, and how long ago (ms). */
  miss: readonly [deg: number, age: number] | null;
  fx?: Fx | undefined;
}

interface Look {
  a: string;
  b: string;
  letters: string;
  face: string;
}

const LOOK: Record<Era, Look> = {
  stars: { a: '#56799c', b: '#0f1b2b', letters: '#d6ebff', face: '#0b1a30' },
  neon: { a: '#40235e', b: '#0c0615', letters: '#ffd6f5', face: '#140a22' },
  debris: { a: '#5e2d2f', b: '#140606', letters: '#ffc8ad', face: '#170708' },
  dust: { a: '#e3b465', b: '#6a4417', letters: '#2a1804', face: '#1e1409' },
  canopy: { a: '#8c643a', b: '#382210', letters: '#f3e8cb', face: '#0e1c0d' },
};

/** The dial is drawn round (150, 150) in a 300 box and set in the middle of the board, a touch larger. */
const C = 150;
const DIAL = `translate(${BOARD.w / 2} ${BOARD.h / 2}) scale(1.05) translate(${-C} ${-C})`;
/** A point `r` from the centre at heading `deg` (0 up, clockwise). */
const at = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180;
  return { x: C + r * Math.sin(a), y: C - r * Math.cos(a) };
};

function wedgePath(width: number, r0: number, r1: number): string {
  const h = width / 2;
  const a = at(-h, r1);
  const b = at(h, r1);
  const c = at(h, r0);
  const d = at(-h, r0);
  return `M${d.x} ${d.y} L${a.x} ${a.y} A ${r1} ${r1} 0 0 1 ${b.x} ${b.y} L${c.x} ${c.y} A ${r0} ${r0} 0 0 0 ${d.x} ${d.y} Z`;
}

const TICKS = (() => {
  let d = '';
  for (let k = 0; k < 72; k++) {
    const long = k % 6 === 0;
    const a = at(k * 5, long ? 113 : 119);
    const b = at(k * 5, 125);
    d += `M${a.x.toFixed(1)} ${a.y.toFixed(1)} L${b.x.toFixed(1)} ${b.y.toFixed(1)} `;
  }
  return d;
})();

/** The dial, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<HeadingView>) {
  const skin = ERA_SKIN[era];
  const look = LOOK[era];
  const id = useSvgId();
  const inMark = onMark(view.d, view.m, view.w);
  const glow = view.s > 0 ? 1 : view.hint && inMark ? 0.85 : 0.5;
  const letters = lang === 'no' ? ['N', 'Ø', 'S', 'V'] : ['N', 'E', 'S', 'W'];
  const miss = view.miss;
  const status =
    view.s === 2
      ? { text: say(lang, '✓ HEADING SET', '✓ KURSEN ER SATT'), tone: 'ok' as const }
      : view.s === 1
        ? { text: say(lang, '✓ ON COURSE', '✓ PÅ KURS'), count: [view.n, view.of] as const, tone: 'ok' as const }
        : miss
          ? { text: say(lang, 'MISSED — WAIT FOR IT', 'BOM – VENT PÅ DEN'), tone: 'bad' as const }
          : { text: say(lang, 'HEADINGS', 'KURSER'), count: [view.n, view.of] as const, hint: say(lang, 'TAP IN THE MARK', 'TRYKK I MERKET') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}bz`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={look.a} />
          <stop offset=".55" stopColor={look.b} />
          <stop offset="1" stopColor={look.a} stopOpacity=".7" />
        </linearGradient>
        <radialGradient id={`${id}fc`} cx=".5" cy=".42" r=".6">
          <stop offset="0" stopColor={look.face} stopOpacity=".6" />
          <stop offset=".7" stopColor={look.face} />
          <stop offset="1" stopColor="#000000" />
        </radialGradient>
        <radialGradient id={`${id}wd`} cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor={accent} stopOpacity=".15" />
          <stop offset="1" stopColor={accent} stopOpacity=".75" />
        </radialGradient>
        <linearGradient id={`${id}nd`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#b9c4ce" />
        </linearGradient>
        <radialGradient id={`${id}cp`} cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset=".45" stopColor={look.a} />
          <stop offset="1" stopColor={look.b} />
        </radialGradient>
      </defs>

      <g transform={DIAL}>
        {era === 'stars' && <ellipse cx={C} cy={C} rx="148" ry="44" fill="none" stroke={skin.haze} strokeOpacity=".18" transform={`rotate(-18 ${C} ${C})`} />}
        <circle cx={C} cy={C + 5} r="143" fill="#000" opacity=".4" />
        <circle cx={C} cy={C} r="143" fill={`url(#${id}bz)`} stroke="#000" strokeOpacity=".5" />
        {era === 'neon' && (
          <>
            <circle cx={C} cy={C} r="141" fill="none" stroke={skin.ink} strokeWidth="6" opacity=".22" />
            <circle cx={C} cy={C} r="141" fill="none" stroke={skin.ink} strokeWidth="1.8" />
            <circle cx={C} cy={C} r="129" fill="none" stroke={skin.haze} strokeWidth="1.4" opacity=".8" />
          </>
        )}
        {era === 'dust' &&
          [22, 67, 112, 157, 202, 247, 292, 337].map((d) => {
            const p = at(d, 136);
            return <circle key={d} cx={p.x} cy={p.y} r="2.2" fill="#fff1c9" stroke="#5a3a10" strokeWidth=".8" />;
          })}
        {era === 'canopy' &&
          [45, 135, 225, 315].map((d) => {
            const p = at(d, 136);
            return <ellipse key={d} cx={p.x} cy={p.y} rx="7" ry="3.2" fill="#6fb85a" transform={`rotate(${d + 90} ${p.x} ${p.y})`} />;
          })}
        <circle cx={C} cy={C} r="128" fill={`url(#${id}fc)`} stroke="#000" strokeOpacity=".6" />
        <circle cx={C} cy={C} r="88" fill="none" stroke={skin.haze} strokeOpacity=".12" />
        <circle cx={C} cy={C} r="50" fill="none" stroke={skin.haze} strokeOpacity=".1" />
        <path d={`M${C} 34 L${C} 266 M34 ${C} L266 ${C}`} stroke={skin.haze} strokeOpacity=".08" />
        <path d={TICKS} stroke={skin.haze} strokeOpacity=".75" strokeWidth="1.4" />
        {letters.map((l, k) => {
          const p = at(k * 90, 135.5);
          return (
            <text key={l} x={p.x} y={p.y + 4} textAnchor="middle" fontSize={TYPE.hint} fontWeight="700" fontFamily={mono} fill={look.letters}>
              {l}
            </text>
          );
        })}

        {/* the mark: a lit wedge, and a notch on the rim; green once the needle is stopped on it */}
        <g transform={`rotate(${view.m} ${C} ${C})`}>
          <path d={wedgePath(view.w, 56, 113)} fill={`url(#${id}wd)`} opacity={glow} stroke={view.s > 0 ? OK : accent} strokeWidth="1.6" strokeOpacity=".9" />
          <path d={`M${C} ${C - 114} L${C - 7} ${C - 127} L${C + 7} ${C - 127} Z`} fill={view.s > 0 ? OK : accent} stroke="#000" strokeOpacity=".4" />
        </g>

        {miss && (
          <g transform={`rotate(${miss[0]} ${C} ${C})`} opacity={Math.max(0, 1 - miss[1] / MISS_SHOW_MS)}>
            <path d={`M${C} ${C - 38} L${C} ${C - 116}`} stroke={BAD} strokeWidth="3" strokeLinecap="round" />
          </g>
        )}

        {era === 'debris' && <path d={`M${C - 90} ${C - 70} L${C - 50} ${C - 40} L${C - 58} ${C - 10} M${C - 50} ${C - 40} L${C - 20} ${C - 48}`} stroke={INK.hi} strokeOpacity=".14" fill="none" />}

        {/* the needle, its shadow, and the cap */}
        <g transform={`translate(3 4) rotate(${view.d} ${C} ${C})`} opacity=".35">
          <path d={`M${C} ${C - 108} L${C + 8} ${C} L${C} ${C + 48} L${C - 8} ${C} Z`} fill="#000" />
        </g>
        <g transform={`rotate(${view.d} ${C} ${C})`}>
          <path d={`M${C} ${C - 108} L${C + 8} ${C} L${C - 8} ${C} Z`} fill={`url(#${id}nd)`} stroke="#0b1220" strokeOpacity=".7" />
          <path d={`M${C} ${C - 108} L${C + 8} ${C} L${C} ${C} Z`} fill="#000" opacity=".12" />
          <path d={`M${C - 8} ${C} L${C + 8} ${C} L${C} ${C + 48} Z`} fill="#4a5866" stroke="#0b1220" strokeOpacity=".7" />
          <circle cx={C} cy={C - 96} r="3.4" fill={skin.haze} />
        </g>
        <circle cx={C} cy={C} r="12" fill={`url(#${id}cp)`} stroke="#000" strokeOpacity=".5" />
        <circle cx={C - 3.5} cy={C - 4} r="3" fill="#ffffff" opacity=".55" />
        {/* the glass over it all */}
        <path d={`M${C - 112} ${C - 10} A 112 112 0 0 1 ${C - 10} ${C - 112} A 130 130 0 0 0 ${C - 112} ${C - 10} Z`} fill="#ffffff" opacity=".07" />
      </g>
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const L = HEADING_LEVELS[level]!;
  const [rand] = useState(() => seeded(Date.now() + level * 2_147_483));
  const [needle] = useState<Needle>(() => {
    const n: Needle = { deg: rand() * 360, dir: 1, mark: 0, turnAt: null, turnsLeft: TURNS_PER_MARK };
    setMark(n, rand);
    return n;
  });
  const stops = useRef(0);
  const lockedUntil = useRef(0);
  const markDue = useRef(false);
  const miss = useRef<{ at: number; deg: number } | null>(null);

  const round = useRound(props, (dt, now, won) => {
    if (won || now < lockedUntil.current) return;
    if (markDue.current) {
      markDue.current = false;
      setMark(needle, rand);
    }
    stepNeedle(needle, dt, now, L, rand);
  });

  const tap = () => {
    if (!round.live) return;
    const now = round.time();
    if (now < lockedUntil.current || (miss.current && now - miss.current.at < COOLDOWN_MS)) return;
    if (onMark(needle.deg, needle.mark, L.mark)) {
      stops.current += 1;
      lockedUntil.current = now + LOCK_MS;
      round.hit();
      round.progress(stops.current / L.stops);
      if (stops.current >= L.stops) round.win();
      else markDue.current = true;
      return;
    }
    miss.current = { at: now, deg: needle.deg };
    round.miss();
  };

  const now = round.now;
  const locked = now < lockedUntil.current;
  const missAge = miss.current ? now - miss.current.at : Infinity;
  const view: HeadingView = {
    t: q(now),
    d: q(needle.deg),
    m: q(needle.mark),
    w: L.mark,
    n: stops.current,
    of: L.stops,
    hint: L.hint,
    s: round.won ? 2 : locked ? 1 : 0,
    miss: miss.current && missAge < MISS_SHOW_MS ? [q(miss.current.deg), q(missAge)] : null,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap to stop the needle', 'Trykk for å stoppe nålen'), down: tap }}
    />
  );
}

/**
 * The how-to: the needle sweeps up to the mark and the finger taps as it enters — the dial locks
 * and a new mark lights further round — then the needle sweeps on and the finger taps there too.
 */
const demo: DemoScript<HeadingView> = {
  ms: 7_200,
  keys: [
    { at: 0, view: { t: 0, d: 250, m: 60, w: 50, n: 0, of: 2, hint: true, s: 0, miss: null } },
    { at: 2_300, view: { d: 412 } },
    { at: 2_310, view: { s: 1, n: 1, fx: [1, 0] }, ease: 'step' },
    { at: 2_800, view: { fx: [0, 0] } },
    { at: 3_000, view: { s: 0, m: 230 }, ease: 'step' },
    { at: 5_300, view: { d: 588 } },
    { at: 5_310, view: { s: 2, n: 2, fx: [1, 0] }, ease: 'step' },
    { at: 5_900, view: { fx: [0, 0] } },
  ],
  finger: [
    { at: 0, x: 262, y: 290, hide: true },
    { at: 900, x: 262, y: 290, hide: false },
    { at: 1_700, x: 236, y: 262 },
    { at: 2_300, x: 236, y: 262, down: true },
    { at: 2_480, x: 236, y: 262, down: false },
    { at: 5_300, x: 236, y: 262, down: true },
    { at: 5_480, x: 236, y: 262, down: false },
    { at: 6_300, x: 262, y: 290, hide: true },
  ],
  steps: [
    { at: 0, en: 'THE NEEDLE TURNS', no: 'NÅLEN GÅR RUNDT' },
    { at: 1_500, en: 'TAP WHEN IT IS IN THE MARK', no: 'TRYKK NÅR DEN ER I MERKET' },
    { at: 3_000, en: 'TWO HEADINGS A ROUND', no: 'TO KURSER HVER RUNDE' },
  ],
};

export const game = thirtyGame<HeadingView>({
  Board,
  View,
  demo,
  words: {
    en: {
      instruction: 'Tap when the turning needle is inside the lit mark. Two headings a round.',
      howMove: 'Watch the needle. Tap anywhere when it is inside the mark.',
    },
    no: {
      instruction: 'Trykk når nålen er inne i det lyse merket. To kurser hver runde.',
      howMove: 'Følg nålen. Trykk hvor som helst når den er inne i merket.',
    },
  },
});
